import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { ThemeService } from 'src/@core/services/theme.service';

declare const intlTelInput: any;

@Component({
  selector: 'hour4u-admin-create-leads-user',
  templateUrl: './create-leads-user.component.html',
  styleUrls: ['./create-leads-user.component.scss']
})
export class CreateLeadsUserComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('phoneInput') phoneInputs!: QueryList<ElementRef>;
  phoneInputInstances: any[] = [];

  private countryChangeHandlers: Array<(() => void) | null> = [];
  private searchCleanupFns: Array<(() => void) | null> = [];
  private phoneInputsSub: any;

  leadsUserCreateForm: FormGroup;
  submitted = false;
  profile = this.appService.getCurrentAccount();
  isUpdate = false;
  checkIcon = 'icon-check';
  plusIcon = 'icon-plus';

  constructor(
    public dialogRef: MatDialogRef<CreateLeadsUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private notify: NotificationService,
    private appService: AppService,
    private jobService: JobService,
    public themeService: ThemeService
  ) { }

  ngOnInit() {
    this.createForm();
  }

  ngAfterViewInit() {
    // Defer so the dialog content is fully in the DOM
    setTimeout(() => this.initIntlTelInputs(), 0);

    this.phoneInputsSub = this.phoneInputs.changes.subscribe(() => {
      setTimeout(() => this.initIntlTelInputs(), 0);
    });
  }

  ngOnDestroy() {
    if (this.phoneInputsSub) {
      this.phoneInputsSub.unsubscribe();
    }
    this.destroyAllItiInstances();
  }

  private destroyAllItiInstances(): void {
    this.phoneInputInstances.forEach((iti, index) => {
      this.teardownItiExtras(index);
      if (iti && typeof iti.destroy === 'function') {
        try {
          iti.destroy();
        } catch (e) {
          // ignore
        }
      }
    });
    this.phoneInputInstances = [];
    this.countryChangeHandlers = [];
    this.searchCleanupFns = [];
  }

  private teardownItiExtras(index: number): void {
    const handler = this.countryChangeHandlers[index];
    const iti = this.phoneInputInstances[index];
    if (iti && handler && typeof iti.off === 'function') {
      try {
        iti.off('countrychange', handler);
      } catch (e) {
        // ignore
      }
    }
    this.countryChangeHandlers[index] = null;

    const cleanup = this.searchCleanupFns[index];
    if (cleanup) {
      cleanup();
    }
    this.searchCleanupFns[index] = null;
  }

  private initIntlTelInputs(): void {
    if (!this.phoneInputs) {
      return;
    }

    this.phoneInputs.forEach((inputRef: ElementRef, index: number) => {
      if (this.phoneInputInstances[index]) {
        return;
      }

      const inputEl = inputRef.nativeElement as HTMLInputElement;
      const iti = intlTelInput(inputEl, {
        initialCountry: 'in',
        separateDialCode: true,
        // All countries available; preferred shown at top
        preferredCountries: ['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca', 'de', 'jp'],
        autoPlaceholder: 'polite',
        formatOnDisplay: true,
        nationalMode: true,
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js'
      });

      this.phoneInputInstances[index] = iti;
      this.attachCountrySearch(iti, inputEl, index);
      this.bindCountryChange(iti, index);

      // Seed country_code from the picker
      const userGroup = this.users.controls[index] as FormGroup;
      if (userGroup) {
        const dial = iti.getSelectedCountryData().dialCode;
        userGroup.get('country_code').setValue(dial, { emitEvent: false });
      }
    });

    // Drop stale instances if users were removed
    if (this.phoneInputInstances.length > this.phoneInputs.length) {
      for (let i = this.phoneInputs.length; i < this.phoneInputInstances.length; i++) {
        this.teardownItiExtras(i);
        const iti = this.phoneInputInstances[i];
        if (iti && typeof iti.destroy === 'function') {
          try {
            iti.destroy();
          } catch (e) {
            // ignore
          }
        }
      }
      this.phoneInputInstances.length = this.phoneInputs.length;
      this.countryChangeHandlers.length = this.phoneInputs.length;
      this.searchCleanupFns.length = this.phoneInputs.length;
    }
  }

  /**
   * Inject a search box into the country dropdown so every country is filterable
   * by name, ISO code, or dial code.
   */
  private attachCountrySearch(iti: any, inputEl: HTMLInputElement, index: number): void {
    const root = inputEl.closest('.iti') as HTMLElement;
    if (!root) {
      return;
    }

    const flagBtn = root.querySelector('.iti__selected-flag') as HTMLElement;
    if (!flagBtn) {
      return;
    }

    const onFlagClick = () => {
      // Dropdown opens after the click handler; inject on next frame
      setTimeout(() => this.injectCountrySearchInput(root), 0);
    };

    flagBtn.addEventListener('click', onFlagClick);
    this.searchCleanupFns[index] = () => {
      flagBtn.removeEventListener('click', onFlagClick);
      const existing = root.querySelector('.create-lead-iti-search');
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
    };
  }

  private injectCountrySearchInput(root: HTMLElement): void {
    const dropdown = root.querySelector('.iti__country-list') as HTMLElement;
    if (!dropdown) {
      return;
    }

    // Reset visibility when reopening
    dropdown.querySelectorAll('.iti__country').forEach((node) => {
      (node as HTMLElement).style.display = '';
    });

    let searchWrap = dropdown.querySelector('.create-lead-iti-search') as HTMLElement;
    if (!searchWrap) {
      searchWrap = document.createElement('div');
      searchWrap.className = 'create-lead-iti-search';
      searchWrap.innerHTML =
        '<input type="text" class="create-lead-iti-search__input" placeholder="Search country or code…" autocomplete="off" />';
      dropdown.insertBefore(searchWrap, dropdown.firstChild);

      const searchInput = searchWrap.querySelector('input') as HTMLInputElement;
      // Keep focus in search; don't close dropdown
      searchInput.addEventListener('click', (e) => e.stopPropagation());
      searchInput.addEventListener('mousedown', (e) => e.stopPropagation());
      searchInput.addEventListener('keydown', (e) => e.stopPropagation());
      searchInput.addEventListener('input', () => {
        const q = (searchInput.value || '').trim().toLowerCase();
        dropdown.querySelectorAll('.iti__country').forEach((node) => {
          const li = node as HTMLElement;
          const name = (li.getAttribute('data-country-name') || li.textContent || '').toLowerCase();
          const dial = (li.getAttribute('data-dial-code') || '').toLowerCase();
          const iso = (li.getAttribute('data-country-code') || '').toLowerCase();
          const match = !q || name.indexOf(q) !== -1 || dial.indexOf(q) !== -1 || iso.indexOf(q) !== -1;
          li.style.display = match ? '' : 'none';
        });
      });
    }

    const input = searchWrap.querySelector('input') as HTMLInputElement;
    if (input) {
      input.value = '';
      setTimeout(() => input.focus(), 0);
    }
  }

  private bindCountryChange(iti: any, index: number): void {
    const handler = () => {
      const userGroup = this.users.controls[index] as FormGroup;
      if (!userGroup) {
        return;
      }
      const dial = iti.getSelectedCountryData().dialCode;
      userGroup.get('country_code').setValue(dial, { emitEvent: false });
      const mobileCtrl = userGroup.get('mobile');
      if (mobileCtrl) {
        mobileCtrl.updateValueAndValidity({ emitEvent: true });
      }
    };
    this.countryChangeHandlers[index] = handler;
    if (typeof iti.on === 'function') {
      iti.on('countrychange', handler);
    }
  }

  createForm() {
    this.leadsUserCreateForm = this.fb.group({
      users: this.fb.array(
        [this.createUserForm(0)],
        [
          this.uniqueFieldValidator('mobile', 'duplicateMobile'),
          this.uniqueFieldValidator('email', 'duplicateEmail')
        ]
      )
    });

    this.users.valueChanges.subscribe(() => {
      this.uniqueFieldValidator('mobile', 'duplicateMobile')(this.users);
      this.uniqueFieldValidator('email', 'duplicateEmail')(this.users);
    });
  }

  get users(): FormArray {
    return this.leadsUserCreateForm.get('users') as FormArray;
  }

  createUserForm(index: number = 0): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      mobile: ['', [Validators.required, this.internationalPhoneValidator(index)]],
      email: ['', [Validators.email]],
      country_code: ['91']
    });
  }

  /**
   * Accept any country number format.
   * Prefers intl-tel-input utils (isValidNumber) when available; otherwise
   * falls back to a sensible international digit-length check (E.164: 7–15).
   */
  private internationalPhoneValidator(index: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value == null ? '' : String(control.value).trim();
      if (!raw) {
        return { required: true };
      }

      const iti = this.phoneInputInstances[index];
      if (iti && typeof iti.isValidNumber === 'function') {
        try {
          // utilsScript may still be loading; isValidNumber returns null/false then
          const valid = iti.isValidNumber();
          if (valid === true) {
            return null;
          }
          // If utils are loaded and number is clearly invalid, fail
          if (valid === false && this.utilsReady(iti)) {
            return { invalidPhone: true };
          }
        } catch (e) {
          // fall through to length check
        }
      }

      const digits = raw.replace(/\D/g, '');
      if (digits.length < 7 || digits.length > 15) {
        return { invalidPhone: true };
      }
      return null;
    };
  }

  private utilsReady(iti: any): boolean {
    try {
      return !!(iti && iti.s && iti.s.utilsScriptLoaded) ||
        !!(typeof window !== 'undefined' && (window as any).intlTelInputUtils);
    } catch {
      return !!(typeof window !== 'undefined' && (window as any).intlTelInputUtils);
    }
  }

  addUser(): void {
    const index = this.users.length;
    this.users.push(this.createUserForm(index));
  }

  removeUser(index: number): void {
    if (this.users.length <= 1) {
      // Always keep at least one lead row
      const first = this.users.controls[0] as FormGroup;
      first.reset({ name: '', mobile: '', email: '', country_code: '91' });
      return;
    }

    this.teardownItiExtras(index);
    const iti = this.phoneInputInstances[index];
    if (iti && typeof iti.destroy === 'function') {
      try {
        iti.destroy();
      } catch (e) {
        // ignore
      }
    }

    this.phoneInputInstances.splice(index, 1);
    this.countryChangeHandlers.splice(index, 1);
    this.searchCleanupFns.splice(index, 1);
    this.users.removeAt(index);

    // Rebuild validators bound to updated indices
    this.users.controls.forEach((ctrl, i) => {
      const mobile = ctrl.get('mobile');
      if (mobile) {
        mobile.setValidators([Validators.required, this.internationalPhoneValidator(i)]);
        mobile.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave(): void {
    this.submitted = true;

    // Force phone revalidation with current country selection
    this.users.controls.forEach((ctrl, index) => {
      const mobile = ctrl.get('mobile');
      if (mobile) {
        mobile.setValidators([Validators.required, this.internationalPhoneValidator(index)]);
        mobile.updateValueAndValidity({ emitEvent: false });
        mobile.markAsTouched();
      }
      ctrl.get('name').markAsTouched();
      if (ctrl.get('email')) {
        ctrl.get('email').markAsTouched();
      }
    });

    if (this.leadsUserCreateForm.invalid) {
      this.submitted = false;
      return;
    }

    const usersFormArray = this.leadsUserCreateForm.get('users') as FormArray;

    const usersWithDialCode = usersFormArray.controls.map((userControl, index) => {
      const phoneInputInstance = this.phoneInputInstances[index];
      let dialCode = userControl.get('country_code').value || '91';
      let mobile = String(userControl.get('mobile').value || '').trim();

      if (phoneInputInstance) {
        const countryData = phoneInputInstance.getSelectedCountryData();
        if (countryData && countryData.dialCode) {
          dialCode = countryData.dialCode;
        }
        // National digits only (no trunk prefix 0). Backend pairs with country_code.
        // Prefer E.164 and strip dial code — NATIONAL format keeps India's leading 0.
        mobile = this.extractNationalMobile(phoneInputInstance, String(dialCode), mobile);
      } else {
        mobile = this.normalizeNationalDigits(mobile);
      }

      return {
        name: userControl.get('name').value,
        email: userControl.get('email') && userControl.get('email').value
          ? userControl.get('email').value
          : '',
        mobile,
        country_code: String(dialCode)
      };
    });

    const obj = {
      caller_details: usersWithDialCode
    };

    this.jobService.createLeadUserHoa(obj, this.profile.entity.id, this.data.project_id).subscribe(
      () => {
        this.submitted = false;
        this.notify.showMessage('Created Leads User successfully!', 3000);
        this.dialogRef.close(true);
      },
      () => {
        this.submitted = false;
      }
    );
  }

  get usersArray(): FormArray {
    return this.leadsUserCreateForm.get('users') as FormArray;
  }

  /**
   * National subscriber digits only — no country code, no trunk prefix "0".
   * Example (IN): input 09819989898 / +91 98199 89898 → "9819989898"
   */
  private extractNationalMobile(iti: any, dialCode: string, fallbackRaw: string): string {
    try {
      if (iti && typeof iti.getNumber === 'function' && (window as any).intlTelInputUtils) {
        const e164 = iti.getNumber((window as any).intlTelInputUtils.numberFormat.E164);
        if (e164) {
          let digits = String(e164).replace(/\D/g, '');
          const dial = String(dialCode || '').replace(/\D/g, '');
          if (dial && digits.indexOf(dial) === 0) {
            digits = digits.slice(dial.length);
          }
          return this.normalizeNationalDigits(digits);
        }
      }
    } catch (e) {
      // fall through
    }
    return this.normalizeNationalDigits(fallbackRaw);
  }

  /** Strip non-digits and leading trunk zeros (e.g. 09819989898 → 9819989898). */
  private normalizeNationalDigits(value: string): string {
    return String(value || '').replace(/\D/g, '').replace(/^0+/, '');
  }

  getMobileError(user: AbstractControl): string {
    const ctrl = user.get('mobile');
    if (!ctrl || !ctrl.touched || ctrl.valid) {
      return '';
    }
    if (ctrl.hasError('required')) {
      return 'Phone number is required';
    }
    if (ctrl.hasError('duplicateMobile')) {
      return 'This phone number is already added';
    }
    if (ctrl.hasError('invalidPhone')) {
      return 'Enter a valid phone number for the selected country';
    }
    return 'Please enter a valid phone number';
  }

  uniqueFieldValidator(fieldName: string, errorKey: string): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null;
      }

      const valueCounts: Record<string, number> = {};

      formArray.controls.forEach((control) => {
        const fieldControl = control.get(fieldName);
        if (!fieldControl) {
          return;
        }
        const fieldValue = (fieldControl.value == null ? '' : String(fieldControl.value)).trim().toLowerCase();
        if (fieldValue) {
          valueCounts[fieldValue] = (valueCounts[fieldValue] || 0) + 1;
        }
      });

      formArray.controls.forEach((control) => {
        const fieldControl = control.get(fieldName);
        if (!fieldControl) {
          return;
        }
        const fieldValue = (fieldControl.value == null ? '' : String(fieldControl.value)).trim().toLowerCase();
        const errors = fieldControl.errors ? { ...fieldControl.errors } : null;

        if (fieldValue && valueCounts[fieldValue] > 1) {
          fieldControl.setErrors({ ...(errors || {}), [errorKey]: true });
        } else if (errors && errors[errorKey]) {
          delete errors[errorKey];
          const remaining = Object.keys(errors).length ? errors : null;
          fieldControl.setErrors(remaining);
        }
      });

      return null;
    };
  }
}
